import axios from "axios";
import { API_BASE_URL } from "../config/api";
import { ConnectionMode } from "../models/flightSearchData";

let airportCache: AirportResponse[] | null = null;
let airportRequest: Promise<AirportResponse[]> | null = null;
const destinationAirportCache = new Map<string, AirportResponse[]>();
const destinationAirportRequests = new Map<string, Promise<AirportResponse[]>>();
const reachableDestinationCache = new Map<string, ReachableAirportResponse[]>();
const reachableDestinationRequests = new Map<string, Promise<ReachableAirportResponse[]>>();
const PREFERRED_CONNECTION_HUBS = [
  "STN",
  "BGY",
  "CRL",
  "DUB",
  "PMI",
  "BCN",
  "FCO",
  "CIA",
  "MXP",
  "MAN",
  "EDI",
];

export interface AirportResponse {
  name: string;
  iata: string;
  city: string;
  country: string;
  country_code: string;
}

export type DestinationReachability = "direct" | "oneStop" | "directAndOneStop";

export interface ReachableAirportResponse extends AirportResponse {
  reachability: DestinationReachability;
  viaAirports: AirportResponse[];
}

const hubPriority = (airport: AirportResponse) => {
  const index = PREFERRED_CONNECTION_HUBS.indexOf(airport.iata);
  return index === -1 ? PREFERRED_CONNECTION_HUBS.length : index;
};

const sortViaAirports = (airports: AirportResponse[]) =>
  [...airports].sort((a, b) => {
    const priorityDiff = hubPriority(a) - hubPriority(b);
    if (priorityDiff !== 0) return priorityDiff;
    return `${a.country}-${a.city}-${a.iata}`.localeCompare(`${b.country}-${b.city}-${b.iata}`);
  });

export class AirportService {
  static async getAirports(query?: string): Promise<AirportResponse[]> {
    if (!query && airportCache) {
      return airportCache;
    }

    if (!query && airportRequest) {
      return airportRequest;
    }

    const request = axios
      .get<AirportResponse[]>(`${API_BASE_URL}/airports`, {
        params: query ? { query } : undefined,
      })
      .then((response) => {
        if (!query) {
          airportCache = response.data;
          airportRequest = null;
        }
        return response.data;
      })
      .catch((error) => {
        if (!query) {
          airportRequest = null;
        }
        throw error;
      });

    if (!query) {
      airportRequest = request;
    }

    return request;
  }

  static async getDestinationAirports(departureAirportCode: string): Promise<AirportResponse[]> {
    const normalizedDepartureAirportCode = departureAirportCode.trim().toUpperCase();
    const cachedDestinations = destinationAirportCache.get(normalizedDepartureAirportCode);
    if (cachedDestinations) {
      return cachedDestinations;
    }

    const pendingRequest = destinationAirportRequests.get(normalizedDepartureAirportCode);
    if (pendingRequest) {
      return pendingRequest;
    }

    const request = axios
      .get<AirportResponse[]>(
        `${API_BASE_URL}/airports/${normalizedDepartureAirportCode}/destinations`
      )
      .then((response) => {
        destinationAirportCache.set(normalizedDepartureAirportCode, response.data);
        destinationAirportRequests.delete(normalizedDepartureAirportCode);
        return response.data;
      })
      .catch((error) => {
        destinationAirportRequests.delete(normalizedDepartureAirportCode);
        throw error;
      });

    destinationAirportRequests.set(normalizedDepartureAirportCode, request);

    return request;
  }

  static async getDestinationAirportsForDepartures(
    departureAirportCodes: string[]
  ): Promise<AirportResponse[]> {
    const destinations = await Promise.all(
      departureAirportCodes.map((departureAirportCode) =>
        AirportService.getDestinationAirports(departureAirportCode)
      )
    );
    const byIata = new Map<string, AirportResponse>();

    destinations.flat().forEach((airport) => {
      byIata.set(airport.iata, airport);
    });

    return Array.from(byIata.values()).sort((a, b) =>
      `${a.country}-${a.city}-${a.iata}`.localeCompare(`${b.country}-${b.city}-${b.iata}`)
    );
  }

  static async getReachableDestinationAirportsForDepartures(
    departureAirportCodes: string[],
    connectionMode: ConnectionMode
  ): Promise<ReachableAirportResponse[]> {
    const normalizedDepartureCodes = departureAirportCodes
      .map((code) => code.trim().toUpperCase())
      .filter(Boolean)
      .sort();
    const cacheKey = `${connectionMode}:${normalizedDepartureCodes.join(",")}`;
    const cachedDestinations = reachableDestinationCache.get(cacheKey);
    if (cachedDestinations) {
      return cachedDestinations;
    }

    const pendingRequest = reachableDestinationRequests.get(cacheKey);
    if (pendingRequest) {
      return pendingRequest;
    }

    const request = AirportService.loadReachableDestinationAirports(
      normalizedDepartureCodes,
      connectionMode
    )
      .then((destinations) => {
        reachableDestinationCache.set(cacheKey, destinations);
        reachableDestinationRequests.delete(cacheKey);
        return destinations;
      })
      .catch((error) => {
        reachableDestinationRequests.delete(cacheKey);
        throw error;
      });

    reachableDestinationRequests.set(cacheKey, request);
    return request;
  }

  private static async loadReachableDestinationAirports(
    departureAirportCodes: string[],
    connectionMode: ConnectionMode
  ): Promise<ReachableAirportResponse[]> {
    const directDestinations = await Promise.all(
      departureAirportCodes.map((departureAirportCode) =>
        AirportService.getDestinationAirports(departureAirportCode)
      )
    );
    const directByIata = new Map<string, AirportResponse>();
    const oneStopByIata = new Map<string, AirportResponse>();
    const viaByIata = new Map<string, AirportResponse[]>();
    const departureCodeSet = new Set(departureAirportCodes);

    directDestinations.flat().forEach((airport) => {
      if (!departureCodeSet.has(airport.iata)) {
        directByIata.set(airport.iata, airport);
      }
    });

    if (connectionMode === ConnectionMode.DirectAndOneStop) {
      const directAirports = Array.from(directByIata.values());
      const preferredHubAirports = directAirports.filter((airport) =>
        PREFERRED_CONNECTION_HUBS.includes(airport.iata)
      );
      const hubAirports = (preferredHubAirports.length > 0 ? preferredHubAirports : directAirports)
        .sort((a, b) => hubPriority(a) - hubPriority(b));
      const secondLevelDestinations = await Promise.all(
        hubAirports.map(async (hubAirport) => ({
          hubAirport,
          destinations: await AirportService.getDestinationAirports(hubAirport.iata),
        }))
      );

      secondLevelDestinations.forEach(({ hubAirport, destinations }) => {
        destinations.forEach((destination) => {
          if (departureCodeSet.has(destination.iata) || destination.iata === hubAirport.iata) {
            return;
          }

          oneStopByIata.set(destination.iata, destination);
          const currentViaAirports = viaByIata.get(destination.iata) ?? [];
          if (!currentViaAirports.some((airport) => airport.iata === hubAirport.iata)) {
            viaByIata.set(destination.iata, sortViaAirports([...currentViaAirports, hubAirport]));
          }
        });
      });
    }

    const reachableByIata = new Map<string, ReachableAirportResponse>();
    directByIata.forEach((airport, iata) => {
      reachableByIata.set(iata, {
        ...airport,
        reachability: "direct",
        viaAirports: [],
      });
    });

    viaByIata.forEach((viaAirports, iata) => {
      if (reachableByIata.has(iata)) return;
      const destinationAirport = oneStopByIata.get(iata);
      if (destinationAirport) {
        reachableByIata.set(iata, {
          ...destinationAirport,
          reachability: "oneStop",
          viaAirports,
        });
      }
    });

    return Array.from(reachableByIata.values()).sort((a, b) =>
      `${a.country}-${a.city}-${a.iata}`.localeCompare(`${b.country}-${b.city}-${b.iata}`)
    );
  }

  static hasCachedDestinationAirports(departureAirportCode: string): boolean {
    return destinationAirportCache.has(departureAirportCode.trim().toUpperCase());
  }

  static hasCachedReachableDestinationAirports(
    departureAirportCodes: string[],
    connectionMode: ConnectionMode
  ): boolean {
    const normalizedDepartureCodes = departureAirportCodes
      .map((code) => code.trim().toUpperCase())
      .filter(Boolean)
      .sort();
    return reachableDestinationCache.has(`${connectionMode}:${normalizedDepartureCodes.join(",")}`);
  }
}
